import { formatDistanceToNowStrict } from 'date-fns';
import { tv, type VariantProps } from 'tailwind-variants';
import type { TrackContentCardClickParams } from '@/app/_lib/types/ContentCardTracking';
import { Image as CustomImage } from '@/app_components/common/Image';
import { Link } from '@/app_components/common/Link';
import { ButtonV2 } from '@/app_components/ui/ButtonV2';
import { DescriptionParser } from '@/app_lib/htmlParser/DescriptionParser';
import type { TrackEventParams } from '@/app_lib/services/Gtm/utils';
import type { ArticlePreview } from '@/app_lib/types/ArticlePreview';
import { ContentPlacement } from '@/app_lib/types/analytics';
import { cn, formatDate } from '@/app_lib/utils/utils';

// biome-ignore lint/style/useComponentExportOnlyModules: <Using these styles for the loaders as well>
export const articleCard = tv({
  slots: {
    articleDate: 'font-graphik typography-xs text-secondary w-fit',
    authorByline: 'typography-sm',
    authorLink: 'font-medium capitalize typography-sm',
    base: 'font-graphik flex-1 group',
    excerpt: 'font-graphik-compact text-secondary line-clamp-2',
    eyebrow: 'typography-xs font-medium uppercase text-secondary hover:underline tracking-[2px] w-fit',
    image: 'aspect-[3/2] object-cover w-full h-full',
    imageContainer: 'aspect-[3/2] relative',
    timeAgo:
      'font-graphik-compact typography-sm font-medium bg-inverse py-xxxs px-xs rounded-sm text-white isolate z-body-popover',
    title: 'font-inria-serif font-bold w-fit',
  },
  variants: {
    variant: {
      featured: {
        base: 'flex flex-col gap-lg',
        excerpt: 'hidden md:block',
        title: 'typography-level-4',
      },
      grid: {
        base: 'flex flex-col gap-lg',
        title: 'typography-level-6',
      },
      gridSaved: {
        base: 'flex flex-col gap-lg',
        image: 'rounded-t-md',
        title: 'typography-level-6',
      },
      hero: {
        authorByline: 'pointer-events-none lg:pointer-events-auto',
        base: `flex flex-col lg:flex-row lg:-mx-3 xl:-mx-6
               gap-md lg:layout-grid-gap 
               bg-secondary hover:bg-helper active:bg-helper p-0 lg:p-sm`,
        // NOTE: Not sure why this isn't using a token
        excerpt: 'text-[18px] line-clamp-none',
        eyebrow: 'typography-sm pointer-events-none lg:pointer-events-auto',
        image: 'lg:group-hover:scale-105 transition-transform duration-500',
        imageContainer: 'overflow-hidden lg:w-1/2 pointer-events-none lg:pointer-events-auto ',
        title: 'typography-level-2',
      },
      newsletter: {
        authorByline: 'font-graphik text-secondary typography-sm',
        authorLink: 'text-secondary',
        base: `flex flex-col gap-sm p-xs border stroke-secondary rounded-md bg-pure 
               shadow-sm hover:shadow-md 
               transition-shadow ease-in-out duration-200`,
        eyebrow:
          'capitalize text-primary typography-level-6 font-graphik-compact font-semibold hover:no-underline hover:text-secondary tracking-normal',
        title: 'font-graphik-compact font-normal',
      },
      railFull: {
        base: 'flex flex-col gap-md',
        // NOTE: Is this the correct pixel value?
        image: 'w-[120px] h-fit',
        imageContainer: 'size-fit',
        title: 'flex-1 font-graphik-compact font-semibold line-clamp-5',
      },
      railSimple: {
        base: 'flex flex-col gap-2 w-full h-full',
      },
      small: {
        base: 'flex flex-col md:flex-row gap-lg',
        // NOTE: Is this the correct pixel value?
        image: 'md:w-[160px] md:h-fit',
        imageContainer: 'size-fit',
        title: 'md:typography-level-6',
      },
      timed: {
        base: 'flex flex-col gap-lg relative',
        timeAgo: 'absolute -top-3 right-2',
        title: 'typography-level-6',
      },
      topic: {},
      trending: {
        base: 'flex lg:flex-col gap-md items-center lg:items-start',
      },
      vertical: {
        base: 'flex flex-col gap-lg',
        excerpt: 'hidden md:block',
        title: 'typography-level-5',
      },
    },
  },
});

export type ArticleCardVariants = VariantProps<typeof articleCard>;

export interface ArticleCardOwnProps<E extends 'div' | typeof Link> extends ArticleCardVariants {
  as?: E;
  className?: string;
  children?: React.ReactNode;
  /** Overrides default `ContentPlacement.ArticleWrapper` for e2e parity with pages-router. */
  dataCy?: string;
}

export type ArticleCardProps<E extends 'div' | typeof Link> = ArticleCardOwnProps<E> &
  Omit<React.ComponentProps<E>, keyof ArticleCardOwnProps<E>>;

/**
 * Polymorphic ArticleCard component.
 *
 * Renders either:
 * - A native `<div/>` element by default
 * - Our custom Link component when used with `as=Link`
 *
 * This component will not accept any props not associated with it's given element type.
 */
export const ArticleCard = <E extends 'div' | typeof Link = 'div'>({
  as,
  className,
  children,
  dataCy,
  variant,
  ...rest
}: ArticleCardProps<E>) => {
  const Element = (as ?? 'div') as React.ElementType;

  return (
    <Element
      className={cn(articleCard().base({ variant }), className)}
      data-cy={dataCy ?? ContentPlacement.ArticleWrapper}
      {...rest}
    >
      {children}
    </Element>
  );
};

export const Image = ({
  className,
  disableDimming = false,
  image,
  imageAlt,
  priority,
  trackingData,
  contentCardTrackingData,
  optimizelyTrackingData,
  url,
  variant,
  onClick,
  dataCy,
}: {
  className?: string;
  disableDimming?: boolean;
  priority?: boolean;
  trackingData?: TrackEventParams;
  contentCardTrackingData?: TrackContentCardClickParams;
  optimizelyTrackingData?: { name: string; properties?: Record<string, string | number> };
  onClick?: (event: React.MouseEvent) => void;
  dataCy?: string;
} & Pick<ArticlePreview, 'image' | 'imageAlt' | 'url'> &
  ArticleCardVariants) => (
  <Link
    className={cn(articleCard().imageContainer({ variant }), className)}
    contentCardTrackingData={contentCardTrackingData}
    data-cy={dataCy}
    href={url}
    onClick={onClick}
    optimizelyTrackingData={optimizelyTrackingData}
    trackingData={trackingData}
  >
    <CustomImage
      alt={imageAlt}
      className={articleCard().image({ variant })}
      data-cy={ContentPlacement.ArticleCardImage}
      priority={priority}
      src={image}
    />
    {/* TODO: Think about moving this into the Image component since it's reused often */}
    {!disableDimming && <div className='top-0 absolute bg-inverse opacity-0 hover:opacity-10 w-full h-full' />}
  </Link>
);

export const Eyebrow = ({
  sectionName,
  sectionUrl,
  variant,
  trackingData,
  contentCardTrackingData,
  optimizelyTrackingData,
  dataCy,
}: {
  sectionName: string;
  sectionUrl: string;
  trackingData?: TrackEventParams;
  contentCardTrackingData?: TrackContentCardClickParams;
  optimizelyTrackingData?: { name: string; properties?: Record<string, string | number> };
  dataCy?: string;
} & ArticleCardVariants) =>
  sectionName &&
  sectionUrl && (
    <Link
      className={cn(articleCard().eyebrow({ variant }))}
      contentCardTrackingData={contentCardTrackingData}
      data-cy={dataCy ?? ContentPlacement.ArticleEyebrow}
      href={sectionUrl}
      optimizelyTrackingData={optimizelyTrackingData}
      trackingData={trackingData}
    >
      {sectionName}
    </Link>
  );

export const Title = ({
  className,
  title,
  url,
  variant,
  trackingData,
  contentCardTrackingData,
  optimizelyTrackingData,
  onClick,
  dataCy,
}: {
  className?: string;
  trackingData?: TrackEventParams;
  contentCardTrackingData?: TrackContentCardClickParams;
  optimizelyTrackingData?: { name: string; properties?: Record<string, string | number> };
  onClick?: (event: React.MouseEvent) => void;
  dataCy?: string;
} & Pick<ArticlePreview, 'title' | 'url'> &
  ArticleCardVariants) => (
  <Link
    className={cn(articleCard().title({ variant }), className)}
    contentCardTrackingData={contentCardTrackingData}
    data-cy={dataCy ?? ContentPlacement.ArticleTitle}
    href={url}
    onClick={onClick}
    optimizelyTrackingData={optimizelyTrackingData}
    recipe='nav'
    trackingData={trackingData}
    variant='primary'
  >
    {title}
  </Link>
);

export const Excerpt = ({ excerpt, variant }: Pick<ArticlePreview, 'excerpt'> & ArticleCardVariants) => (
  <div
    className={cn(articleCard().excerpt({ variant }))}
    data-cy={ContentPlacement.ArticleCardExcerpt}
  >
    <DescriptionParser content={excerpt} />
  </div>
);

export const AuthorByline = ({
  authors,
  variant,
  trackingData,
  contentCardTrackingData,
  optimizelyTrackingData,
}: {
  trackingData?: TrackEventParams;
  contentCardTrackingData?: TrackContentCardClickParams;
  optimizelyTrackingData?: { name: string; properties?: Record<string, string | number> };
} & Pick<ArticlePreview, 'authors'> &
  ArticleCardVariants) => (
  <span
    className={cn(articleCard().authorByline({ variant }))}
    data-cy={ContentPlacement.ArticleCardByline}
  >
    <span data-cy={ContentPlacement.AuthorByText}>By </span>
    {authors
      .filter((author) => author.name?.trim())
      .map((author, index, visible) => {
        let prependedContent = '';

        if (index > 0 && index === visible.length - 1) {
          prependedContent = ' and ';
        } else if (index > 0) {
          prependedContent = ', ';
        }

        const isPlain = author.withoutLinks || !author.url?.trim();

        return (
          <span key={`${author.url || author.name}-${index}`}>
            {prependedContent}
            {isPlain ? (
              <span
                className={articleCard().authorLink({ variant })}
                data-cy={ContentPlacement.BylineAuthor}
              >
                {author.name}
              </span>
            ) : (
              <ButtonV2
                className={articleCard().authorLink({ variant })}
                contentCardTrackingData={contentCardTrackingData}
                data-cy={ContentPlacement.BylineAuthor}
                href={author.url}
                isTertiary
                optimizelyTrackingData={optimizelyTrackingData}
                trackingData={trackingData && { data: { ...trackingData, eventLabel: `${author.name} : ` } }}
              >
                {author.name}
              </ButtonV2>
            )}
          </span>
        );
      })}
  </span>
);

export const CardDate = ({
  dateGmt,
  useRelativeTime = false,
  variant,
  hidden = false,
  dataCy,
}: { useRelativeTime?: boolean; hidden?: boolean; dataCy?: string } & Pick<ArticlePreview, 'dateGmt'> &
  ArticleCardVariants) =>
  hidden ? null : (
    <span
      className={cn(articleCard().articleDate({ variant }))}
      data-cy={dataCy ?? ContentPlacement.ArticleCardDate}
    >
      {useRelativeTime ? formatDistanceToNowStrict(dateGmt, { addSuffix: true }) : formatDate(dateGmt)}
    </span>
  );

export const CardTimeAgo = ({ dateGmt, variant }: Pick<ArticlePreview, 'dateGmt'> & ArticleCardVariants) => {
  return (
    <span
      className={articleCard().timeAgo({ variant })}
      data-cy={ContentPlacement.TimeAgo}
    >
      {formatDistanceToNowStrict(new Date(dateGmt), { addSuffix: true })}
    </span>
  );
};
